import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { buildPath, NOTIFICATIONS } from '../config';
import { NotificationsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { BellIcon, TasksIcon, ComplaintsIcon, WarningIcon } from './Icons';


/**
 * Top-bar notification "bell".
 * =============================
 *
 * Renders the bell button in the AppShell top bar (top-right).  On an
 * interval it polls ``GET /api/notification/inbox/`` — the aggregated
 * feed built by ``NotificationInboxView`` (see
 * ``notification/api_views.py``) — and shows:
 *
 *   * a red badge with the number of UNSEEN items on the bell, and
 *   * a dropdown panel that lists the notifications grouped by kind
 *     (Tasks assigned to me, Complaints assigned to me, Routine tasks,
 *      Tasks I delegated, Overdue / Due-today / Urgent).
 *
 * "Unseen" is a purely client-side concept
 * ----------------------------------------
 * We deliberately did NOT add a server-side "read" model / migration.
 * Instead each notification item carries a stable ``key``
 * (``"<type>:<id>"``) and we persist the set of keys the user has
 * already SEEN in ``localStorage`` (namespaced per-user).  The badge
 * counts items whose key isn't in that set.  Opening the dropdown marks
 * everything currently shown as seen.  This keeps the feature
 * zero-migration and always in sync with the live source rows — a task
 * that gets completed simply drops out of the feed on the next poll.
 *
 * Polling, not WebSockets
 * -----------------------
 * The rest of the app (e.g. Chat) already uses interval polling per the
 * product brief, so we follow the same pattern here.  Cadence lives in
 * ``NOTIFICATIONS.POLL_INTERVAL_MS`` (``config.js``) so it stays in
 * lock-step with chat.  The API call is marked ``silent`` (see
 * ``NotificationsAPI.inbox``) so a transient backend hiccup during a
 * background poll never yanks the user off-page or spams a global
 * error toast.
 */

// localStorage key namespace for the per-user "seen" set.
const SEEN_KEY_PREFIX = 'taskydo.notif.seen.';


function iconForGroup(iconKey) {
  switch (iconKey) {
    case 'complaints':
      return ComplaintsIcon;
    case 'warning':
      return WarningIcon;
    case 'tasks':
    default:
      return TasksIcon;
  }
}


/** Map a feed item to the SPA route it should deep-link to. */
function targetPathForItem(item) {
  switch (item.type) {
    case 'task':
      return buildPath.taskDetails(item.id);
    case 'routine_task':
      return buildPath.routineTaskEdit(item.id);
    case 'complaint':
      return buildPath.complaintDetails(item.id);
    default:
      return null;
  }
}


/** Human-friendly "3 days ago" style relative time. */
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mon = Math.round(day / 30);
  return `${mon}mo ago`;
}


export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Per-user localStorage key so two people on the same machine don't
  // clobber each other's "seen" state.
  const seenStorageKey = useMemo(
    () => SEEN_KEY_PREFIX + (user?.id ?? 'anon'),
    [user?.id],
  );

  // The set of item keys the user has already seen.  Loaded lazily from
  // localStorage; kept in a ref so the polling loop can read it without
  // re-subscribing on every change.
  const [seenKeys, setSeenKeys] = useState(() => {
    try {
      const raw = localStorage.getItem(SEEN_KEY_PREFIX + (user?.id ?? 'anon'));
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  const rootRef = useRef(null);

  // Flatten every item across groups into one list (used to compute the
  // unread badge and to update the "seen" set when the panel opens).
  const allItems = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const g of groups) {
      for (const it of g.items || []) {
        if (seen.has(it.key)) continue;
        seen.add(it.key);
        out.push(it);
      }
    }
    return out;
  }, [groups]);

  const unreadCount = useMemo(
    () => allItems.filter((it) => !seenKeys.has(it.key)).length,
    [allItems, seenKeys],
  );

  // ---- Fetch the feed --------------------------------------------------
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await NotificationsAPI.inbox();
      setGroups(Array.isArray(data?.groups) ? data.groups : []);
    } catch {
      // Silent — background poll.  Keep the last-known groups on screen.
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll on mount + on an interval.  Also refresh when the tab regains
  // focus so a user coming back to the tab sees a fresh count quickly.
  useEffect(() => {
    if (!user) return undefined;
    load();
    const id = setInterval(load, NOTIFICATIONS.POLL_INTERVAL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, load]);

  // ---- Close on outside-click / Escape --------------------------------
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Persist the "seen" set whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(seenStorageKey, JSON.stringify([...seenKeys]));
    } catch {
      /* storage full / disabled — badge just won't persist, no crash */
    }
  }, [seenKeys, seenStorageKey]);

  // Mark everything currently visible as seen (called when the panel is
  // opened).  We keep already-seen keys so items that scroll out of the
  // feed and come back aren't re-flagged.
  const markAllSeen = useCallback(() => {
    setSeenKeys((prev) => {
      const next = new Set(prev);
      for (const it of allItems) next.add(it.key);
      return next;
    });
  }, [allItems]);

  const toggleOpen = useCallback(() => {
    setOpen((wasOpen) => {
      const nowOpen = !wasOpen;
      if (nowOpen) {
        // Opening: refresh + mark seen so the badge clears.
        load();
        markAllSeen();
      }
      return nowOpen;
    });
  }, [load, markAllSeen]);

  const goTo = useCallback(
    (item) => {
      const path = targetPathForItem(item);
      setOpen(false);
      if (path) navigate(path);
    },
    [navigate],
  );

  if (!user) return null;

  const totalItems = allItems.length;

  return (
    <div className="ty-notif" ref={rootRef}>
      <button
        type="button"
        className="ty-notif__btn"
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : 'Notifications'
        }
        aria-haspopup="true"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <BellIcon size={20} />
        {unreadCount > 0 && (
          <span className="ty-notif__badge" aria-hidden>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="ty-notif__panel" role="dialog" aria-label="Notifications">
          <header className="ty-notif__panel-head">
            <span className="ty-notif__panel-title">Notifications</span>
            <span className="ty-notif__panel-count">
              {totalItems > 0 ? `${totalItems} items` : ''}
            </span>
          </header>

          <div className="ty-notif__panel-body">
            {loading && groups.length === 0 && (
              <div className="ty-notif__empty">Loading…</div>
            )}

            {!loading && groups.length === 0 && (
              <div className="ty-notif__empty">
                🎉 You're all caught up — no notifications.
              </div>
            )}

            {groups.map((group) => {
              const GroupIcon = iconForGroup(group.icon);
              return (
                <section className="ty-notif__group" key={group.key}>
                  <div className="ty-notif__group-head">
                    <span className="ty-notif__group-icon" aria-hidden>
                      <GroupIcon size={15} />
                    </span>
                    <span className="ty-notif__group-label">{group.label}</span>
                    <span className="ty-notif__group-count">{group.count}</span>
                  </div>

                  <ul className="ty-notif__list">
                    {(group.items || []).map((item) => {
                      const isNew = !seenKeys.has(item.key);
                      return (
                        <li key={`${group.key}:${item.key}`}>
                          <button
                            type="button"
                            className={
                              'ty-notif__item' +
                              (isNew ? ' ty-notif__item--new' : '')
                            }
                            onClick={() => goTo(item)}
                          >
                            <span className="ty-notif__item-title">
                              {item.title}
                              {item.urgency &&
                                item.urgency !== 'default' && (
                                  <span className="ty-notif__tag ty-notif__tag--urgent">
                                    {item.urgency}
                                  </span>
                                )}
                            </span>
                            {item.subtitle && (
                              <span className="ty-notif__item-sub">
                                {item.subtitle}
                              </span>
                            )}
                            <span className="ty-notif__item-meta">
                              {item.created_by && (
                                <span className="ty-notif__item-by">
                                  {item.created_by}
                                </span>
                              )}
                              {item.end_date && (
                                <span className="ty-notif__item-due">
                                  Due {item.end_date}
                                </span>
                              )}
                              <span className="ty-notif__item-time">
                                {relativeTime(item.date)}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                    {group.count > (group.items || []).length && (
                      <li className="ty-notif__more">
                        +{group.count - group.items.length} more…
                      </li>
                    )}
                  </ul>
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
